import { Info, Warning } from "@phosphor-icons/react";
import { CodeBlock, DocSection } from "./DocComponents";

const BASE_URL = process.env.REACT_APP_BACKEND_URL;
const TRACKER_DOMAIN = process.env.REACT_APP_TRACKER_DOMAIN || "";
const TRACKER_BASE = TRACKER_DOMAIN
  ? `${TRACKER_DOMAIN.replace(/\/$/, "")}/c`
  : `${BASE_URL}/api/click`;

/**
 * Tracking Links Guide tab. Static documentation on how the click
 * redirect server works and the legal subId parameters. The pub's
 * `subId` value gets templated into the example links + warnings so
 * they can copy-paste into their integration.
 */
export function TrackingLinksTab({ subId }) {
  return (
    <div className="space-y-6">
      <div className="border border-blue-200 bg-blue-50/50 rounded-md p-4 flex gap-3">
        <Info size={18} className="text-[#002FA7] shrink-0 mt-0.5" />
        <div className="text-sm text-slate-700 leading-relaxed">
          <p className="font-medium text-slate-900 mb-1">How Tracking Links Work</p>
          <p>Every tracking link goes through our click redirect server. This allows us to track each individual click with a unique ID (RID), so you can see exactly which clicks converted and which didn't.</p>
          <p className="mt-2 text-xs text-slate-500">Your subId1: <span className="font-mono font-bold text-[#002FA7]">{subId}</span> — this is embedded in every tracking link and identifies you as the publisher.</p>
        </div>
      </div>

      <div className="border border-slate-200 rounded-md p-5">
        <h3 className="font-heading text-sm font-semibold text-slate-900 mb-4">Your Tracking Link Format</h3>
        <div className="bg-slate-900 text-slate-100 rounded-md px-4 py-3 text-xs font-mono break-all mb-3">
          {TRACKER_BASE}/&#123;brand_id&#125;?subId1={subId}
        </div>
        <p className="text-xs text-slate-500 mb-4">Go to the <strong>Brands</strong> page to copy ready-made tracking links for each brand. The subId1 is already embedded.</p>

        <DocSection title="What Happens When Someone Clicks Your Link">
          <ol className="text-xs text-slate-600 space-y-3 list-decimal ml-4 leading-relaxed">
            <li>
              <strong>Click is logged on our server</strong><br/>
              A unique <strong>RID (Request ID)</strong> is generated for this click. We record the timestamp, your subId1, brand, and click metadata.
            </li>
            <li>
              <strong>User is redirected to the brand</strong><br/>
              Our server sends a <strong>302 redirect</strong> to the brand's website via the affiliate tracking system. The URL includes:<br/>
              <code className="bg-slate-100 px-1.5 py-0.5 rounded text-[10px] block mt-1 break-all">subId1={subId}&sharedId=&#123;RID&#125;</code>
              <span className="text-slate-400 block mt-0.5">subId1 identifies your traffic. sharedId is the unique click RID for matching later.</span>
            </li>
            <li>
              <strong>User browses and may purchase</strong><br/>
              The brand's cookie tracks the user. If they make a purchase within the attribution window, the conversion is recorded.
            </li>
            <li>
              <strong>The network fires a postback to RedClub</strong><br/>
              The postback contains <code className="bg-slate-100 px-1 rounded text-[10px]">subId1={subId}</code> (to identify you) and <code className="bg-slate-100 px-1 rounded text-[10px]">sharedId=&#123;RID&#125;</code> (to match the exact click).
            </li>
            <li>
              <strong>We match the click to the conversion</strong><br/>
              Using the RID, we find the original click record and update it with: sale amount, your commission earnings, and conversion status (Approved/Pending).
            </li>
            <li>
              <strong>You see it in your Click Log</strong><br/>
              Go to <strong>Reports → Click Log</strong> to see every click. Converted clicks show the sale amount, your earnings, and status. Non-converting clicks show as "Click" with $0 earnings.
            </li>
          </ol>
        </DocSection>
      </div>

      <div className="border border-slate-200 rounded-md p-5">
        <h3 className="font-heading text-sm font-semibold text-slate-900 mb-4">Understanding All Tracking Parameters</h3>
        <p className="text-xs text-slate-500 mb-4">These parameters can be appended to your tracking link. subId1 is set automatically. The rest are optional and available for your use.</p>
        <div className="border border-slate-200 rounded-md overflow-hidden">
          <table className="w-full text-xs">
            <thead><tr className="bg-slate-50 border-b border-slate-200">
              <th className="text-left px-4 py-2.5 font-bold text-slate-500">Parameter</th>
              <th className="text-left px-4 py-2.5 font-bold text-slate-500">Required</th>
              <th className="text-left px-4 py-2.5 font-bold text-slate-500">Usage</th>
              <th className="text-left px-4 py-2.5 font-bold text-slate-500">Example</th>
            </tr></thead>
            <tbody>
              <tr className="border-b border-slate-100 bg-[#002FA7]/5">
                <td className="px-4 py-3 font-mono text-[#002FA7] font-bold">subId1</td>
                <td className="px-4 py-3"><span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-50 text-red-700">AUTO-SET</span></td>
                <td className="px-4 py-3 text-slate-700"><strong>Your Publisher ID.</strong> Identifies all your traffic. This is always set to <code className="bg-white px-1 rounded font-mono border border-slate-200">{subId}</code>. <span className="text-red-600 font-semibold">Never change this.</span> Brands cannot see this value.</td>
                <td className="px-4 py-3 font-mono text-slate-500">{subId}</td>
              </tr>
              <tr className="border-b border-slate-100">
                <td className="px-4 py-3 font-mono text-slate-700">subId2</td>
                <td className="px-4 py-3 text-slate-400 text-[10px]">Optional</td>
                <td className="px-4 py-3 text-slate-700">Sub-affiliate tracking. Use for campaign name, traffic source, or placement. Appears in your reports. <strong>Brands cannot see this.</strong></td>
                <td className="px-4 py-3 font-mono text-slate-500">facebook_spring</td>
              </tr>
              <tr className="border-b border-slate-100">
                <td className="px-4 py-3 font-mono text-slate-700">subId3</td>
                <td className="px-4 py-3 text-slate-400 text-[10px]">Optional</td>
                <td className="px-4 py-3 text-slate-700">Additional sub-tracking. Use for creative ID, geo target, A/B test variant, etc. <strong>Brands cannot see this.</strong></td>
                <td className="px-4 py-3 font-mono text-slate-500">banner_300x250</td>
              </tr>
              <tr className="border-b border-slate-100 bg-slate-50/50">
                <td className="px-4 py-3 font-mono text-slate-500">sharedId</td>
                <td className="px-4 py-3"><span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-blue-50 text-blue-700">AUTO</span></td>
                <td className="px-4 py-3 text-slate-700"><strong>Auto-generated by our system.</strong> This is the unique click RID used to match clicks to conversions. Do not set manually. Both you and the brand can see this value in reporting.</td>
                <td className="px-4 py-3 font-mono text-slate-500">A1CCB3747E7B4F49</td>
              </tr>
              <tr className="border-b border-slate-100">
                <td className="px-4 py-3 font-mono text-slate-700">deepLink</td>
                <td className="px-4 py-3 text-slate-400 text-[10px]">Optional</td>
                <td className="px-4 py-3 text-slate-700"><strong>Direct URL to a specific product or page</strong> on the brand's site. If omitted, the user lands on the brand's default landing page. Must be URL-encoded.</td>
                <td className="px-4 py-3 font-mono text-slate-500 text-[10px] break-all">https%3A%2F%2Fmacpaw.com%2Fcleanmymac</td>
              </tr>
              <tr className="border-b border-slate-100">
                <td className="px-4 py-3 font-mono text-slate-700">adId</td>
                <td className="px-4 py-3 text-slate-400 text-[10px]">Optional</td>
                <td className="px-4 py-3 text-slate-700"><strong>Ad identifier</strong> — specify which ad or creative the link is for. If omitted, redirects to the brand's program landing page.</td>
                <td className="px-4 py-3 font-mono text-slate-500">101010</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="border border-slate-200 rounded-md p-5">
        <h3 className="font-heading text-sm font-semibold text-slate-900 mb-4">Examples</h3>

        <DocSection title="Basic Link (default landing page)">
          <p className="text-xs text-slate-500 mb-2">Sends traffic to the brand's default landing page. subId1 auto-set, sharedId auto-generated.</p>
          <CodeBlock>{`${TRACKER_BASE}/1733?subId1=${subId}`}</CodeBlock>
        </DocSection>

        <DocSection title="With Campaign Tracking (subId2)">
          <p className="text-xs text-slate-500 mb-2">Track which campaign or traffic source is driving conversions.</p>
          <CodeBlock>{`${TRACKER_BASE}/1733?subId1=${subId}&subId2=facebook_spring_sale&subId3=banner_728x90`}</CodeBlock>
        </DocSection>

        <DocSection title="Deep Link to a Specific Product Page">
          <p className="text-xs text-slate-500 mb-2">Send users directly to a specific product page instead of the brand's homepage. The deepLink must be URL-encoded.</p>
          <CodeBlock>{`${TRACKER_BASE}/1733?subId1=${subId}&subId2=email_promo&deepLink=https%3A%2F%2Fmacpaw.com%2Fcleanmymac`}</CodeBlock>
          <p className="text-xs text-slate-500 mt-1">The user will land on the CleanMyMac page on MacPaw, and your commission is tracked.</p>
        </DocSection>

        <DocSection title="With Ad Identifier">
          <p className="text-xs text-slate-500 mb-2">If the brand has specific ads or creatives, use adId to track which ad performed.</p>
          <CodeBlock>{`${TRACKER_BASE}/1733?subId1=${subId}&adId=101010&subId2=my_website_sidebar`}</CodeBlock>
        </DocSection>

        <DocSection title="Full Example (All Parameters)">
          <CodeBlock>{`${TRACKER_BASE}/1733?subId1=${subId}&subId2=email_blast_april&subId3=variation_a&deepLink=https%3A%2F%2Fmacpaw.com%2Fsetapp&adId=101010`}</CodeBlock>
          <p className="text-xs text-slate-500 mt-1">Our server will add <code className="bg-slate-100 px-1 rounded font-mono">sharedId=&#123;auto-generated RID&#125;</code> before redirecting to the brand.</p>
        </DocSection>
      </div>

      <div className="border border-amber-200 bg-amber-50/50 rounded-md p-4 flex gap-3">
        <Warning size={18} className="text-amber-600 shrink-0 mt-0.5" />
        <div className="text-xs text-slate-700 space-y-2">
          <p><strong>Important Rules:</strong></p>
          <ul className="list-disc ml-4 space-y-1">
            <li><strong>Always use the tracking links from your Brands page</strong> — they have your subId1 pre-set. Do not construct tracking URLs manually.</li>
            <li><strong>Never modify subId1</strong> — it must always be your assigned publisher ID (<code className="font-mono font-bold">{subId}</code>). Changing it will break conversion tracking.</li>
            <li><strong>Do not set sharedId manually</strong> — our click redirect server generates a unique RID for each click and passes it as sharedId automatically.</li>
            <li><strong>subId2 and subId3 are optional</strong> — use them freely for your own internal tracking (campaigns, placements, traffic sources).</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
